import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Calendar } from "lucide-react";
import { useEffect } from "react";

import { Link } from "@/components/link";
import { directPurchaseApi } from "@/features/auth/onboarding/api/directPurchase.api";
import { sendProductPurchaseNotification } from "@/features/auth/subscription/api/productPurchaseNotification.api";
import { CheckEmailScreen } from "@/features/auth/subscription/components/CheckEmailScreen";
import { useGoogleTagManager } from "@/features/auth/subscription/hooks/useGoogleTagManager.ts";
import {
  consumeFirstPromoterReferralEmail,
  trackFirstPromoterReferral,
} from "@/features/auth/subscription/utils/firstPromoter";
import {
  buildPurchaseEvent,
  hasFiredPurchase,
  markPurchaseFired,
} from "@/features/auth/subscription/utils/purchaseEvent";
import {
  allCountKeys,
  allRequestListKeys,
  invalidateQueryKeys,
} from "@/hooks/queryKeys";
import { usePageTitle } from "@/hooks/usePageTitle";
import { getPlatform, pushDataLayer } from "@/lib/dataLayer";

import {
  isFirstDraft,
  isFullyManaged,
  resolveCalendarUrl,
} from "./subscription-calendar";

function OnboardingCalendarEmbed() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const origin = search.origin ?? null;
  const planName = search.planName ?? null;
  const checkEmail = search.checkEmail === "true";

  usePageTitle(planName ?? "Subscription");
  const calendarUrl = resolveCalendarUrl(origin, planName);
  const fullyManaged = isFullyManaged(origin, planName);
  const firstDraft = isFirstDraft(planName);

  useGoogleTagManager("GTM-5X67HTS");

  useEffect(() => {
    const transactionId = search.sub_id ?? search.inv_id;

    if (!transactionId || hasFiredPurchase(transactionId)) return;

    pushDataLayer(
      buildPurchaseEvent({
        displayCurrency: search.currency,
        origin,
        planName: search.planName,
        planPriceMajor: search.price,
        platform: getPlatform(),
        transactionId,
        valueMajor: search.value,
      }),
    );
    markPurchaseFired(transactionId);
  }, [
    search.sub_id,
    search.inv_id,
    search.currency,
    search.planName,
    search.price,
    search.value,
    origin,
  ]);

  useEffect(() => {
    if (firstDraft && !checkEmail) {
      navigate({ to: "/" });
    }
  }, [firstDraft, checkEmail, navigate]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type === "calendar.booking_completed" ||
        event.data?.meetingBookSucceeded
      ) {
        queryClient.invalidateQueries({ queryKey: ["user"] });
        invalidateQueryKeys(queryClient, [
          ...allRequestListKeys,
          ...allCountKeys,
        ]);
        navigate({ search: { filter: "all", tab: "active" }, to: "/requests" });
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [navigate, queryClient]);

  useEffect(() => {
    const referralEmail = consumeFirstPromoterReferralEmail();

    if (referralEmail) {
      trackFirstPromoterReferral(referralEmail);
    }
  }, []);

  useEffect(() => {
    const checkEmail = async (): Promise<void> => {
      try {
        const pendingData = localStorage.getItem("pendingPurchaseNotification");

        if (pendingData) {
          const purchaseData = JSON.parse(pendingData);
          const ownerEmail = purchaseData.ownerEmail;

          if (!ownerEmail) {
            console.error("No ownerEmail found in pending data");

            return;
          }

          const result = await directPurchaseApi.checkEmail(ownerEmail);

          if (result.exists && result.userId) {
            await sendProductPurchaseNotification({
              ...purchaseData,
              ownerId: result.userId,
              platformId: result.projectId,
            });

            localStorage.removeItem("pendingPurchaseNotification");
            console.log("Product purchase notification sent successfully");
          } else {
            console.log(
              "Email does not exist in database, keeping data in localStorage",
            );
          }
        }
      } catch (error) {
        console.error("Error sending notification email:", error);
      }
    };

    checkEmail();
  }, []);

  if (checkEmail) {
    return (
      <CheckEmailScreen
        billingCycle={search.billingCycle}
        currency={search.currency}
        planName={search.planName}
        price={search.price}
      />
    );
  }
  if (firstDraft) return null;

  return (
    <div className="min-h-screen bg-bg-secondary">
      <div className="max-w-full">
        {!fullyManaged && (
          <div className="flex gap-xl rounded-xl border border-[#D0D5DD] bg-white p-xl max-w-[1000px] mx-auto">
            <div className="grid size-5xl shrink-0 place-items-center rounded-full bg-bg-brand-secondary">
              <Calendar
                className="text-featured-icon-light-fg-brand"
                size={20}
              />
            </div>
            <div className="flex flex-1 flex-col gap-xs">
              <span className="font-semibold text-lg text text-text-brand-secondary">
                Set Yourself Up for Success with a Quick Onboarding Call
              </span>
              <span className="font-normal text-sm text-text-tertiary">
                Booking an onboarding call ensures you get the most out of your
                design subscription by learning how to submit briefs effectively
                and achieve the best results. This quick session sets you up for
                success, helping streamline your workflow and maximize the
                impact of every design project.
              </span>
              <span className="font-bold text-sm text-button-tertiary-fg">
                See{" "}
                <Link
                  className="text-button-tertiary-color-fg"
                  href="https://help.nlc.com/en/articles/10750963-onboarding-at-nlc-everything-you-need-to-know"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  everything you need to know
                </Link>{" "}
                about onboarding with us.
              </span>
            </div>
          </div>
        )}
        <iframe
          className="w-full mx-auto max-w-[1000px] block"
          src={calendarUrl}
          style={{ border: "none", minHeight: "785px" }}
          title="Schedule an Onboarding Call"
        />
        <div className="flex justify-end max-w-[1000px] mx-auto mt-4">
          <button
            className="inline-flex items-center gap-2 px-8 py-3 text-base font-semibold text-text-tertiary hover:text-text-brand-secondary cursor-pointer transition-colors"
            onClick={async () => {
              await queryClient.invalidateQueries({ queryKey: ["user"] });
              navigate({ to: "/" });
            }}
            type="button"
          >
            Skip
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/subscription-success")({
  component: OnboardingCalendarEmbed,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    billingCycle?: string;
    checkEmail?: string;
    coupon?: string;
    currency?: string;
    inv_id?: string;
    origin?: string;
    planName?: string;
    price?: string;
    sub_id?: string;
    value?: string;
  } => ({
    billingCycle: search.billingCycle as string | undefined,
    checkEmail: search.checkEmail as string | undefined,
    coupon: search.coupon as string | undefined,
    currency: search.currency as string | undefined,
    inv_id: search.inv_id as string | undefined,
    origin: search.origin as string | undefined,
    planName: search.planName as string | undefined,
    price: search.price as string | undefined,
    sub_id: search.sub_id as string | undefined,
    value: search.value as string | undefined,
  }),
});
